import {z} from "zod/v4-mini"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {mapFields} from "@cm/utils"
import {meshGeometryAttributeSetLikeMarker, GeometryAttributeSetNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import {meshLike} from "#template/nodes/object/mesh/fundamental"
import type {MeshLikeNode} from "#template/nodes/object/mesh/fundamental"
import type {NumberLike} from "#template/nodes/types"
import {numberLike} from "#template/nodes/number/fundamental"
import type {TemplateNodeDefinition} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"

import type {MeshGeometryAttributeSetLikeNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import {createMeshGeometryAttributeData, createMeshGeometryAttributeSetData, isGeometryAttributeRefData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeSetData} from "#template/nodes/geometry-graph/interfaces"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {AttributeRef, GeomBuilderContext} from "#template/geometry-processing/geometry-graph"
import {meshToGeom} from "#template/geometry-processing/geometry-graph"
import {geometryAttributeRefDataSetFromAttributeRefSets} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"
import type {AbstractMeshData} from "#template/interfaces/mesh-data"

export type MeshGeometryAttributeSetMeshLikeInputParameters = {
    mesh: MeshLikeNode | null
    numUVChannels: NumberLike | null
}

const definition: TemplateNodeDefinition<MeshGeometryAttributeSetMeshLikeInputParameters> = {
    nodeClass: "MeshGeometryAttributeSetMeshLikeInput",
    validation: {
        parameters: z.object({
            mesh: z.nullable(meshLike),
            numUVChannels: z.nullable(numberLike),
        }),
        returns: getTemplateNodeReturns(),
    },
}

function meshGeometryAttributeRefDataSetFromAbstractMeshData(
    abstractMeshData: Omit<AbstractMeshData, "contentHash">,
    numUVChannels: number,
    ctx: GeomBuilderContext,
) {
    const meshToAttrRefSetForGraphType = (graphType: "display" | "render") => {
        const meshToGeomRefSet = meshToGeom(
            ctx,
            graphType === "display" ? abstractMeshData.displayGeometryGraph : abstractMeshData.renderGeometryGraph,
            numUVChannels,
        )

        const uvsRefSet: Record<string, AttributeRef> = {}
        for (let i = 0; i < numUVChannels; i++) {
            uvsRefSet[`uv${i}`] = meshToGeomRefSet.uvs[i]
        }

        return {
            position: meshToGeomRefSet.position,
            normal: meshToGeomRefSet.normal,
            materialID: meshToGeomRefSet.materialID,
            uvs: uvsRefSet,
        }
    }

    return geometryAttributeRefDataSetFromAttributeRefSets(
        meshToAttrRefSetForGraphType("render"),
        meshToAttrRefSetForGraphType("display"),
        abstractMeshData.displayGeometryGraphResources,
    )
}

@registerTemplateGraphNode
export class MeshGeometryAttributeSetMeshLikeInput
    extends GeometryAttributeSetNode<2, MeshGeometryAttributeSetMeshLikeInputParameters>
    implements MeshGeometryAttributeSetLikeNode
{
    get [meshGeometryAttributeSetLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    protected override hasValidParameters(
        parameters: MeshGeometryAttributeSetMeshLikeInputParameters,
    ): parameters is MeshGeometryAttributeSetMeshLikeInputParameters & NonNullableParameters<MeshGeometryAttributeSetMeshLikeInputParameters> {
        return parameters.mesh !== null && parameters.numUVChannels !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<MeshGeometryAttributeSetMeshLikeInputParameters>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
        ctx: GeomBuilderContext,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeSetData<2> | null>; invalidator?: BuilderInlet<null>} {
        const [attributeSetDataValid, attributeSetDataInvalid] = scope.branch(
            scope.pureLambda(
                scope.tuple(evaluator.evaluate(parameters.mesh), evaluator.evaluate(parameters.numUVChannels), ctx),
                ([mesh, numUVChannels, ctx]) => {
                    if (!mesh || !numUVChannels) return null
                    return createMeshGeometryAttributeSetData(
                        mapFields(
                            meshGeometryAttributeRefDataSetFromAbstractMeshData(mesh.completeMeshData.abstract, numUVChannels, ctx),
                            (attrRefDataOrAttrRefDataSet) =>
                                isGeometryAttributeRefData(attrRefDataOrAttrRefDataSet)
                                    ? createMeshGeometryAttributeData(attrRefDataOrAttrRefDataSet)
                                    : createMeshGeometryAttributeSetData(
                                          mapFields(attrRefDataOrAttrRefDataSet, (attrRefData) => createMeshGeometryAttributeData(attrRefData)),
                                      ),
                        ),
                    )
                },
                "setupMeshAttributeSetData",
            ),
        )

        return {evaluatedGeometryData: attributeSetDataValid, invalidator: attributeSetDataInvalid}
    }
}
